import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setPassword, setUsername } from "../features/login/loginSlice";
import { setAnimationEnabled } from "../features/plume/plumeSlice";
import { updateFacilities, updateSelectFacilitie } from "../features/facilities/facilitiesSlice";
import { useServices } from "../context/ServicesContext";
import {
  setSnackBarOpen,
  setSnackbarData,
} from "../features/snackbar/snackbar";
import { setLoading } from "../features/loading/loadingSlice";
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader/Loader";
import CustomizedSnackbar from "../components/SnackBar/SnackBar";

// Estilos dos inputs modernos - tema claro
const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "#e2e8f0",
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: "#1e4a8e",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1e4a8e",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 4px rgba(30, 74, 142, 0.1)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748b",
    "&.Mui-focused": {
      color: "#1e4a8e",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#1e293b",
    padding: "16px 14px",
    "&::placeholder": {
      color: "#94a3b8",
    },
  },
  "& .MuiInputAdornment-root": {
    color: "#64748b",
  },
};

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loginService } = useServices();
  const { username, password } = useAppSelector((state) => state.login);
  const { isOpen, snackbarData } = useAppSelector((state) => state.snackbar);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUsername(event.target.value));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPassword(event.target.value));
  };

  const [entraLoginUrl, setEntraLoginUrl] = useState<string | null>(null);
  const entraHandled = useRef(false);

  const showError = useCallback(
    (message: string) => {
      dispatch(setSnackBarOpen(true));
      dispatch(setSnackbarData({ message, severity: "error" }));
    },
    [dispatch]
  );

  // Pós-login comum ao login por senha e ao Microsoft (mesma resposta do back)
  const completeLogin = useCallback(
    (data: any) => {
      // Reset da seleção de projeto persistida — cada login começa "limpo".
      localStorage.removeItem("airesflow.facilities.v1");
      dispatch(updateFacilities([]));
      dispatch(updateSelectFacilitie(-1));
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      if (data.min_search_date) {
        localStorage.setItem("min_search_date", data.min_search_date);
      } else {
        localStorage.removeItem("min_search_date");
      }
      // Salva permissões do usuário
      localStorage.setItem("can_download_plume", String(data.can_download_plume ?? true));
      localStorage.setItem("can_edit_receptors", String(data.can_edit_receptors ?? false));
      localStorage.setItem("user_modules", JSON.stringify(data.modules ?? []));
      localStorage.setItem("is_staff", String(data.is_staff ?? false));
      // Preferências de UI persistidas por usuário
      const animPref = data.plume_animation_enabled ?? true;
      localStorage.setItem("plume_animation_enabled", String(animPref));
      dispatch(setAnimationEnabled(animPref));
      // Guarda o id do usuário pra poder fazer PATCH em /users/{id}/
      if (data.user_id) localStorage.setItem("user_id", String(data.user_id));
      dispatch(setUsername(""));
      dispatch(setPassword(""));
      dispatch(setLoading(false));
      navigate("/");
    },
    [dispatch, navigate]
  );

  // Login Microsoft: mostra o botão se o back estiver com ele ligado, e trata o
  // retorno do callback (?entra_code=... ou ?entra=<motivo>).
  useEffect(() => {
    loginService.getAuthConfig().then((cfg) => {
      setEntraLoginUrl(cfg.entra_enabled ? cfg.entra_login_url : null);
    });

    if (entraHandled.current) return; // StrictMode roda o effect 2x; o código é de uso único
    entraHandled.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("entra_code");
    const reason = params.get("entra");
    if (!code && !reason) return;
    // Tira o código da URL (histórico/referer) antes de qualquer coisa
    window.history.replaceState({}, "", window.location.pathname);

    if (code) {
      dispatch(setLoading(true));
      loginService
        .entraExchange(code)
        .then(completeLogin)
        .catch((err: any) => {
          dispatch(setLoading(false));
          showError(err.message || "Não foi possível entrar com a Microsoft. Tente novamente.");
        });
      return;
    }
    const messages: Record<string, string> = {
      pendente: "Sua conta Microsoft foi reconhecida, mas o acesso ainda precisa ser liberado pela equipe da Aires.",
      negado: "Acesso não autorizado para esta conta ou empresa. Fale com o suporte da Aires.",
      cancelado: "Login com a Microsoft cancelado.",
      erro: "Não foi possível entrar com a Microsoft. Tente novamente.",
    };
    showError(messages[reason as string] ?? messages.erro);
  }, [loginService, dispatch, completeLogin, showError]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(setLoading(true));
    await loginService
      .login(username, password)
      .then(completeLogin)
      .catch((err: any) => {
        dispatch(setLoading(false));
        // Mantém o e-mail digitado; limpa só a senha pra o usuário poder tentar de novo.
        dispatch(setPassword(""));
        dispatch(setSnackBarOpen(true));
        dispatch(setSnackbarData({ message: err.message, severity: "error" }));
      });
  };

  return (
    <>
      <Loader />
      <CustomizedSnackbar
        open={isOpen}
        message={snackbarData.message}
        severity={snackbarData.severity}
      />

      {/* Container principal com fundo gradiente claro */}
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Efeitos de fundo animados - tons claros */}
        <Box
          sx={{
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: `
              radial-gradient(circle at 20% 80%, rgba(30, 74, 142, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(30, 74, 142, 0.05) 0%, transparent 40%)
            `,
            animation: "pulse 8s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1) rotate(0deg)" },
              "50%": { transform: "scale(1.1) rotate(5deg)" },
            },
          }}
        />

        {/* Partículas flutuantes - tons azul da empresa */}
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: `${20 + i * 15}px`,
              height: `${20 + i * 15}px`,
              borderRadius: "50%",
              background: `rgba(30, 74, 142, ${0.12 - i * 0.015})`,
              top: `${10 + i * 15}%`,
              left: `${5 + i * 18}%`,
              animation: `float${i} ${6 + i * 2}s ease-in-out infinite`,
              [`@keyframes float${i}`]: {
                "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
                "50%": { transform: `translateY(${-20 - i * 10}px) translateX(${10 + i * 5}px)` },
              },
            }}
          />
        ))}

        {/* Card de Login */}
        <Box
          sx={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: "420px",
            margin: "20px",
          }}
        >
          {/* Glow effect atrás do card - tons azuis da empresa */}
          <Box
            sx={{
              position: "absolute",
              inset: "-2px",
              background: "linear-gradient(135deg, #1e4a8e, #3b82f6, #1e4a8e, #3b82f6)",
              backgroundSize: "400% 400%",
              borderRadius: "28px",
              filter: "blur(25px)",
              opacity: 0.3,
              animation: "gradient 8s ease infinite",
              "@keyframes gradient": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
              },
            }}
          />

          {/* Card principal - tema claro */}
          <Box
            sx={{
              position: "relative",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              border: "1px solid rgba(30, 74, 142, 0.1)",
              padding: "40px",
              boxShadow: "0 25px 50px -12px rgba(30, 74, 142, 0.15), 0 0 0 1px rgba(30, 74, 142, 0.05)",
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <img
                src="/aires.png"
                alt="Aires Logo"
                style={{
                  width: "280px",
                  height: "auto",
                  filter: "drop-shadow(0 4px 20px rgba(30, 74, 142, 0.2))",
                }}
              />
            </Box>

            {/* Subtítulo */}
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#64748b",
                mb: 4,
                fontSize: "0.9rem",
                letterSpacing: "0.5px",
              }}
            >
              Acesse sua conta para continuar
            </Typography>

            {/* Formulário */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              <TextField
                sx={inputStyles}
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={username}
                onChange={handleUsernameChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#64748b", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                sx={inputStyles}
                required
                fullWidth
                name="password"
                label="Senha"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={handlePasswordChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#64748b", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "#64748b" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Botão de Login */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #1e4a8e 0%, #2563eb 100%)",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 15px rgba(30, 74, 142, 0.35)",
                  transition: "all 0.3s ease",
                  display: "flex",
                  gap: 1,
                  "&:hover": {
                    background: "linear-gradient(135deg, #163a70 0%, #1d4ed8 100%)",
                    boxShadow: "0 6px 25px rgba(30, 74, 142, 0.45)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                <LoginIcon sx={{ fontSize: 20 }} />
                Entrar
              </Button>

              {/* Login Microsoft (Entra) — só aparece com ENTRA_LOGIN_ENABLED no back */}
              {entraLoginUrl && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2 }}>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(30, 74, 142, 0.15)" }} />
                    <Typography sx={{ color: "#94a3b8", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      ou
                    </Typography>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(30, 74, 142, 0.15)" }} />
                  </Box>
                  <Button
                    component="a"
                    href={entraLoginUrl}
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1.4,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      color: "#3c4043",
                      borderColor: "rgba(39, 48, 108, 0.18)",
                      bgcolor: "#ffffff",
                      display: "flex",
                      gap: 1.25,
                      "&:hover": { bgcolor: "#f6f9fc", borderColor: "rgba(39, 48, 108, 0.35)" },
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                      <rect width="10" height="10" x="1" y="1" fill="#f25022" />
                      <rect width="10" height="10" x="12" y="1" fill="#7fba00" />
                      <rect width="10" height="10" x="1" y="12" fill="#00a4ef" />
                      <rect width="10" height="10" x="12" y="12" fill="#ffb900" />
                    </svg>
                    Entrar com Microsoft
                  </Button>
                </>
              )}
            </Box>

            {/* Rodapé do card */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid rgba(30, 74, 142, 0.1)",
                textAlign: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.75rem",
                }}
              >
                Monitoramento Ambiental Inteligente
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Versão no canto */}
        <Typography
          sx={{
            position: "absolute",
            bottom: 20,
            right: 20,
            color: "rgba(30, 74, 142, 0.3)",
            fontSize: "0.7rem",
          }}
        >
          v1.6.0
        </Typography>
      </Box>
    </>
  );
};

export default Login;
